/*Copyright (c) 2025 por Tecnologías de Información MALMUR y empresas afiliadas. Todos los derechos reservados.
Este software es información confidencial y propietaria de Copyright (c) 2025 por Tecnologías de Información MALMUR
 y sus afiliadas. ("Información Confidencial"). Usted no puede divulgar dicha Información Confidencial, 
 y solo puede usar dicha Información Confidencial de acuerdo con los términos del acuerdo de licencia que celebró con MALMUR.*/

/*Copyright (c) 2025 by Tecnologías de Información MALMUR and affiliated companies. All Rights Reserved.
This software is confidential and proprietary information of Copyright (c) 2025 by Tecnologías de Información 
MALMUR and its affiliates. ("Confidential Information"). You may not disclose such Confidential Information, 
and may only use such Confidential Information in accordance with the terms of the license agreement you entered 
into with MALMUR.*/



import Box from "components/generals/Box";
import Button from "components/generals/Button";
import ButtonDialog from "components/generals/ButtonDialog";
import MuiCheckbox from "components/generals/Checkbox";
import DefaultCell from "components/generals/DefaultCell";
import { Modal } from "components/generals/Modal";
import { CoreAPI_URLs } from "constants/http.const";
import DataTable from "dashboard-template/examples/Tables/DataTable";
import useHttp from "hooks/http.hook";
import { TableProps } from "interfaces";
import { HeaderEnum, PharmacyInterface, UserRolesEnum } from "@malmur-technologies/medicalsystem-shared";
import { FC, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { FormActionType } from "types/form.enum";
import { getNewPharmacyDataContent } from "utils/model.utils";
import PharmacyForm from "components/forms/PharmacyForm";
import PermissionWrapper from "components/generals/PermissionWrapper";
import { EntityPermissions, PermissionsUser } from "types";
import { selectIsPatientOrDoctor } from "store/slices/auth.slice";

/**
 * Pharmacy table component for displaying and managing pharmacy records.
 * 
 * Features:
 * - Displays pharmacy information in sortable data table
 * - Conditional column visibility based on user role
 * - Edit pharmacy functionality via modal with PharmacyForm
 * - Delete pharmacy with confirmation dialog and list update
 * - Permission-based access control
 * - Checkbox column visibility toggle for patients/doctors
 * - Automatic modal action type management
 * 
 * Table Columns:
 * - Pharmacy name
 * - Address/location
 * - Phone number
 * - Status/availability
 * - Checkbox (hidden for patients/doctors)
 * - Action buttons
 * 
 * Security:
 * - Uses HeaderEnum.RESOURCE_ID for secure identification
 * - Permission-based rendering via PermissionWrapper
 * - Field hiding for patients and doctors
 * - Validates entity permissions before operations
 * 
 * @param {TableProps<PharmacyInterface>} props - Table properties
 * @param {PharmacyInterface[]} props.modelList - Array of pharmacy records
 * @param {Function} props.setModelList - Callback to update pharmacy list
 * 
 * @returns {JSX.Element} Pharmacy data table with role-based visibility
 */
const PharmacyTable: FC<TableProps<PharmacyInterface>> = ({
  modelList,
  setModelList,
  ...props
}) => {
  const [selectedPharmacy, setSelectedPharmacy] = useState<PharmacyInterface>(
    getNewPharmacyDataContent()
  );
  const [activeEditPharmacyModal, setActiveEditPharmacyModal] = useState<boolean>(false);
  const [modalAction, setModalAction] = useState<FormActionType>();
  const { t } = useTranslation();

  const [_isLoading, _error, sendRequest] = useHttp();

  const shouldHideFields = useSelector(selectIsPatientOrDoctor);

  const openModalHandler = (pharmacy: PharmacyInterface) => {
    setSelectedPharmacy(pharmacy);
    setActiveEditPharmacyModal(true);
  };

  const deleteHandler = (pharmacyTemplate: PharmacyInterface) => {
    setSelectedPharmacy(pharmacyTemplate);

    sendRequest<boolean, PharmacyInterface>(
      CoreAPI_URLs.PHARMACIES_GENERAL_OPERATION,
      onPharmacyTemplateDeleteHandler,
      "DELETE",
      JSON.stringify(pharmacyTemplate),
      [{ header: HeaderEnum.RESOURCE_ID, value: pharmacyTemplate.id }],
      { payload: pharmacyTemplate, successMessage: t("pharmacyTable.pharmacyDeleted") }
    );
  };

  const onPharmacyTemplateDeleteHandler = (
    result: boolean,
    pharmacyTemplate: PharmacyInterface
  ) => {
    const indexList = modelList.indexOf(pharmacyTemplate);
    modelList.splice(indexList, 1);
    setModelList([...modelList]);
  };

  const getUITable = (list: PharmacyInterface[]) => {
    const baseColumns = [];
    if (!shouldHideFields) {
      baseColumns.push(
        {
          Header: t("pharmacyTable.eprescribe"),
          accessor: "ePrescribe",
          disableSortBy: true,
          Cell: ({ value }: any) => <MuiCheckbox sx={{paddingLeft:1.5}} label={""} checked={value} disabled />,
        },
        {
          Header: t("pharmacyTable.mailOrder"),
          accessor: "mailOrderPharmacy",
          disableSortBy: true,
          Cell: ({ value }: any) => <MuiCheckbox  sx={{paddingLeft:1.5}} label={""} checked={value} disabled />,
        },
        {
          Header: t("pharmacyTable.ncpdpid"),
          accessor: "ncpdID",
          sortIndicatorType: "numeric",
          Cell: ({ value }: any) => <DefaultCell value={value} />,
        }
      );
    }
    baseColumns.push(
      {
        Header: t("pharmacyTable.name"),
        accessor: "displayName",
        sortIndicatorType: "az",
        Cell: ({ value }: any) => <DefaultCell value={value} />,
      },
      {
        Header: t("pharmacyTable.address"),
        accessor: "address",
        sortIndicatorType: "az",
        Cell: ({ value }: any) => <DefaultCell value={value} />,
      },
      {
        Header: t("pharmacyTable.city"),
        accessor: "city",
        sortIndicatorType: "az",
        Cell: ({ value }: any) => <DefaultCell value={value} />,
      },
      {
        Header: t("pharmacyTable.state"),
        accessor: "state",
        sortIndicatorType: "az",
        Cell: ({ value }: any) => <DefaultCell value={value} />,
      },
      {
        Header: t("pharmacyTable.actions"),
        accessor: "id",
        disableSortBy: true,
        Cell: ({ value }: any) => (
          <>
            <Button
              name="viewPharmacyButton"
              variant="contained"
              color="info"
              sx={{ marginRight: 1 }}
              onClick={() => {
                const pharmacy = list.find((p) => p.id === value);
                if (pharmacy) {
                  setModalAction(FormActionType.MODIFY);
                  openModalHandler(pharmacy);
                }
              }}
            >
              {t("pharmacyTable.viewAction")}
            </Button>

            <PermissionWrapper
              requiredPermissions={[PermissionsUser[EntityPermissions.PHARMACY].DELETE]}
            >
              <ButtonDialog
                onAcceptHandle={() => {
                  const pharmacy = list.find((p) => p.id === value);
                  if (pharmacy) {
                    deleteHandler(pharmacy);
                  }
                }}
                name="deletePharmacyButton"
                buttonText={t("pharmacyTable.deleteAction")}
                title={t("pharmacyTable.deleteConfirmation")}
                description={t("pharmacyTable.description")}
                labelledbyID={"PharmacyDelete"}
                describedbyID={"PharmacyDescriptionId"}
              />
            </PermissionWrapper>
          </>
        ),
      }
    );

    return {
      columns: baseColumns,
      rows: list,
    };
  };

  const dataTableData = useMemo(() => getUITable(modelList), [modelList, shouldHideFields]);

  return (
    <>
      <Box mt={2} mb={2} width="100%" display="flex" justifyContent="flex-end">
        <PermissionWrapper
          requiredPermissions={[PermissionsUser[EntityPermissions.PHARMACY].CREATE]}
        >
          <Button
            name="newPharmacyButton"
            type="button"
            variant="contained"
            color="primary"
            onClick={() => {
              setModalAction(FormActionType.NEW);
              setSelectedPharmacy(getNewPharmacyDataContent);
              setActiveEditPharmacyModal(true);
            }}
          >
            {t("pharmacyForm.newPharmacy")}
          </Button>
        </PermissionWrapper>
      </Box>
      <DataTable {...props} table={dataTableData} canSearch isLoading={props.isLoading}
        onSortChange={props.onSortChange}
        currentSortBy={props.currentSortBy}
        currentSortOrder={props.currentSortOrder}
      />
      <Modal
        open={activeEditPharmacyModal}
        onClose={() => {
          setActiveEditPharmacyModal(false);
        }}
      >
        <PharmacyForm
          modelList={modelList}
          setModelList={setModelList}
          action={modalAction}
          model={selectedPharmacy}
          onCloseAction={() => {
            setActiveEditPharmacyModal(false);
          }}
          isModal={true}
        />
      </Modal>
    </>
  );
};

export default PharmacyTable;
